import { type IUseFilterProps, useFilters } from "@fluidattacks/design";
import type { IFilterOptions, IOptionsProps } from "@fluidattacks/design";
import _ from "lodash";
import * as React from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type {
  EventStatusEnum,
  EventType,
  GetEventsConnectionQueryVariables,
} from "gql/graphql";
import { selectOptionType } from "pages/group/events/constants";
import type { IEventData } from "pages/group/events/types";
import { useFiltersStorage } from "storage/filters";
import { getCheckedFilters, getNewFilter } from "utils/filters";
import { castEventStatus } from "utils/format-helpers";

const useEventsFilters = ({
  dataset,
  loading,
  setBackFilters,
}: Readonly<{
  dataset: IEventData[];
  loading: boolean;
  setBackFilters: React.Dispatch<
    React.SetStateAction<GetEventsConnectionQueryVariables["filters"]>
  >;
}>): IUseFilterProps<IEventData> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();
  const options: IOptionsProps<IEventData>[] = React.useMemo(
    (): IOptionsProps<IEventData>[] => [
      {
        filterOptions: [
          {
            key: "eventDate",
            label: t("searchFindings.tabEvents.date"),
            type: "dateRange",
          },
        ],
        label: t("searchFindings.tabEvents.date"),
      },
      {
        filterOptions: [
          {
            key: "eventType",
            label: t("searchFindings.tabEvents.type"),
            options: selectOptionType,
            type: "checkboxes",
          },
        ],
        label: t("searchFindings.tabEvents.type"),
      },
      {
        filterOptions: [
          {
            key: "eventStatus",
            label: t("searchFindings.tabEvents.status"),
            options: [
              {
                label: t(castEventStatus("VERIFICATION_REQUESTED").status),
                value: "PENDING",
              },
              {
                label: t(castEventStatus("CREATED").status),
                value: "UNSOLVED",
              },
              { label: t(castEventStatus("SOLVED").status), value: "SOLVED" },
            ],
            type: "checkboxes",
          },
        ],
        label: t("searchFindings.tabEvents.status"),
      },
      {
        filterOptions: [
          {
            key: "closingDate",
            label: t("searchFindings.tabEvents.dateClosed"),
            type: "dateRange",
          },
        ],
        label: t("searchFindings.tabEvents.dateClosed"),
      },
    ],
    [t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("events") as string | null;
  }, [getValues]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("events", value);
    },
    [setValues],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IEventData>[]): void => {
      if (!loading) {
        const newFiltersArray = newFilters as IFilterOptions<IEventData>[];

        const eventDate = getNewFilter(newFiltersArray, "eventDate");
        const reportDateBefore =
          _.isEmpty(eventDate?.maxValue) && _.isNil(eventDate?.maxValue)
            ? undefined
            : String(eventDate?.maxValue);

        const reportDateAfter =
          _.isEmpty(eventDate?.minValue) && _.isNil(eventDate?.minValue)
            ? undefined
            : String(eventDate?.minValue);

        const closedDate = getNewFilter(newFiltersArray, "closingDate");
        const closedDateBefore =
          _.isEmpty(closedDate?.maxValue) && _.isNil(closedDate?.maxValue)
            ? undefined
            : String(closedDate?.maxValue);

        const closedDateAfter =
          _.isEmpty(closedDate?.minValue) && _.isNil(closedDate?.minValue)
            ? undefined
            : String(closedDate?.minValue);

        const types = getCheckedFilters(
          newFiltersArray,
          "eventType",
        ) as EventType[];
        const status = getCheckedFilters(
          newFiltersArray,
          "eventStatus",
        ) as EventStatusEnum[];

        const newBackFilters: Partial<
          GetEventsConnectionQueryVariables["filters"]
        > = {
          closedDateAfter,
          closedDateBefore,
          reportDateAfter,
          reportDateBefore,
          status,
          types,
        };

        setBackFilters(
          (prev): GetEventsConnectionQueryVariables["filters"] => ({
            ...prev,
            ...newBackFilters,
          }),
        );
      }
    },
    [loading, setBackFilters],
  );

  return useFilters({
    dataset,
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options,
    setValues: handleSetValues,
  });
};

export { useEventsFilters };
